<template>
  <div class="yoy-expense-widget-container">
    <div v-if="loading" class="yoy-expense-widget-loading">Loading...</div>
    <div v-else-if="error" class="yoy-expense-widget-error">{{ error }}</div>
    <div v-else class="yoy-expense-widget-content">
      <div class="yoy-expense-widget-summary">
        <div class="yoy-expense-widget-summary-item">
          <div class="yoy-expense-widget-year-label">{{ previousYear }}</div>
          <div class="yoy-expense-widget-year-value">{{ formatCurrency(previousYearTotal) }}</div>
        </div>
        <div class="yoy-expense-widget-summary-item">
          <div class="yoy-expense-widget-year-label">{{ currentYear }}</div>
          <div class="yoy-expense-widget-year-value yoy-expense-widget-current">{{ formatCurrency(currentYearTotal) }}</div>
        </div>
      </div>
      <div class="yoy-expense-widget-change" :class="changeClass">
        <span class="yoy-expense-widget-change-label">{{ changeLabel }}</span>
        <span class="yoy-expense-widget-change-value">{{ changePercentage }}%</span>
      </div>
      <div class="yoy-expense-widget-chart">
        <div class="yoy-expense-widget-chart-bars">
          <div 
            v-for="(month, index) in months" 
            :key="index"
            class="yoy-expense-widget-chart-month"
          >
            <div class="yoy-expense-widget-chart-month-label">{{ month }}</div>
            <div class="yoy-expense-widget-chart-bars-container">
              <div 
                class="yoy-expense-widget-chart-bar yoy-expense-widget-chart-bar-previous"
                :style="{ height: getBarHeight(previousYearData[index]) + '%' }"
                :title="formatCurrency(previousYearData[index])"
              ></div>
              <div 
                class="yoy-expense-widget-chart-bar yoy-expense-widget-chart-bar-current"
                :style="{ height: getBarHeight(currentYearData[index]) + '%' }"
                :title="formatCurrency(currentYearData[index])"
              ></div>
            </div>
          </div>
        </div>
        <div class="yoy-expense-widget-chart-legend">
          <div class="yoy-expense-widget-legend-item">
            <span class="yoy-expense-widget-legend-color yoy-expense-widget-legend-previous"></span>
            <span class="yoy-expense-widget-legend-text">{{ previousYear }}</span>
          </div>
          <div class="yoy-expense-widget-legend-item">
            <span class="yoy-expense-widget-legend-color yoy-expense-widget-legend-current"></span>
            <span class="yoy-expense-widget-legend-text">{{ currentYear }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/YearOverYearExpenseWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'YearOverYearExpenseWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const previousYear = currentYear - 1;
    
    return {
      currentYear,
      previousYear,
      currentYearData: new Array(12).fill(0),
      previousYearData: new Array(12).fill(0),
      loading: true,
      error: null
    };
  },
  computed: {
    currentYearTotal() {
      return this.currentYearData.reduce((sum, val) => sum + val, 0);
    },
    previousYearTotal() {
      return this.previousYearData.reduce((sum, val) => sum + val, 0);
    },
    changePercentage() {
      if (this.previousYearTotal === 0) return this.currentYearTotal > 0 ? 100 : 0;
      const change = ((this.currentYearTotal - this.previousYearTotal) / this.previousYearTotal) * 100;
      return Math.abs(change).toFixed(1);
    },
    changeLabel() {
      if (this.currentYearTotal > this.previousYearTotal) return 'Increase';
      if (this.currentYearTotal < this.previousYearTotal) return 'Decrease';
      return 'No Change';
    },
    changeClass() {
      if (this.currentYearTotal > this.previousYearTotal) return 'yoy-expense-widget-change-increase';
      if (this.currentYearTotal < this.previousYearTotal) return 'yoy-expense-widget-change-decrease';
      return 'yoy-expense-widget-change-neutral';
    },
    months() {
      return ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
    },
    maxValue() {
      const allValues = [...this.currentYearData, ...this.previousYearData];
      return Math.max(...allValues, 1); // At least 1 to avoid division by zero
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        // Get current year date range
        const currentYearStart = new Date(this.currentYear, 0, 1);
        const currentYearEnd = new Date(this.currentYear, 11, 31, 23, 59, 59);
        
        // Get previous year date range
        const previousYearStart = new Date(this.previousYear, 0, 1);
        const previousYearEnd = new Date(this.previousYear, 11, 31, 23, 59, 59);
        
        // Fetch bills and expenses for both years
        const [currentYearBills, previousYearBills] = await Promise.all([
          this.fetchExpensesForYear(currentYearStart, currentYearEnd),
          this.fetchExpensesForYear(previousYearStart, previousYearEnd)
        ]);
        
        // Process current year data
        this.currentYearData = this.processMonthlyData(currentYearBills, this.currentYear);
        
        // Process previous year data
        this.previousYearData = this.processMonthlyData(previousYearBills, this.previousYear);
      } catch (error) {
        logger.error('Error fetching year over year expense data:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    async fetchExpensesForYear(startDate, endDate) {
      try {
        // Fetch only expenses (not bills)
        const expenses = await ApiService.getBillsAndExpenses(this.companyId, { view: 'Expenses' });
        const expensesArray = Array.isArray(expenses) ? expenses : [];
        
        // Filter expenses within date range, excluding voided and cancelled
        return expensesArray.filter(expense => {
          if (!expense.date) return false;
          const expenseDate = new Date(expense.date);
          return expenseDate >= startDate && expenseDate <= endDate && 
                 expense.type === 'Expense' &&
                 !expense.voided &&
                 expense.status !== 'voided' &&
                 expense.status !== 'cancelled';
        });
      } catch (error) {
        logger.error('Error fetching expenses for year:', error);
        return [];
      }
    },
    processMonthlyData(bills, year) {
      const monthlyData = new Array(12).fill(0);
      
      bills.forEach(bill => {
        if (!bill.date) return;
        const billDate = new Date(bill.date);
        const month = billDate.getMonth(); // 0-11
        
        if (billDate.getFullYear() === year && month >= 0 && month < 12) {
          // Use originalAmount for bills/expenses (this represents the expense amount)
          monthlyData[month] += parseFloat(bill.originalAmount || bill.totalAmount || 0);
        }
      });
      
      return monthlyData;
    },
    getBarHeight(value) {
      if (this.maxValue === 0) return 0;
      return Math.max((value / this.maxValue) * 100, 2); // Minimum 2% for visibility
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

